/**
 * Qué tipo de recibo es un cobro que se repite (luz, seguro de coche…), para
 * que Gavi sepa qué truco contar. Orden (síntesis de la tropa, 21-oct-2026,
 * notas/pagos-fijos-gavi/sintesis-director.md §2): (1) el comercio está en
 * la lista; (2) palabras del comercio o de la categoría; (3) si no cuadra,
 * `null` y la ficha pregunta «¿Qué es esto?» una sola vez. Funciones puras.
 */

export const TIPOS_RECIBO = [
  'luz',
  'gas',
  'tele',
  'suscripcion',
  'seguro-coche',
  'seguro-hogar',
  'seguro-salud',
  'tarjeta',
  'otro',
] as const;

export type TipoRecibo = (typeof TIPOS_RECIBO)[number];

export function esTipoRecibo(valor: unknown): valor is TipoRecibo {
  return (
    typeof valor === 'string' &&
    (TIPOS_RECIBO as readonly string[]).includes(valor)
  );
}

export function esSeguro(tipo: TipoRecibo | null): boolean {
  return (
    tipo === 'seguro-coche' ||
    tipo === 'seguro-hogar' ||
    tipo === 'seguro-salud'
  );
}

/** Luz, gas: lo que se paga depende de lo que se gasta. */
export function esConsumo(tipo: TipoRecibo | null): boolean {
  return tipo === 'luz' || tipo === 'gas';
}

/** Minúsculas, sin acentos ni signos, y sin «S.A.», «SAU», «Energía»… */
export function normalizarComercio(nombre: string): string {
  return ` ${nombre
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(
      /\b(s ?a ?u?|s ?l ?u?|sociedad anonima|comercializadora|energia|espana|iberia|recibo|rebut|adeudo|cargo|pago|domiciliacion)\b/g,
      ' ',
    )
    .replace(/\s+/g, ' ')
    .trim()} `;
}

type Regla = { tipo: TipoRecibo; palabras: readonly string[] };

const ELECTRICAS = [
  'iberdrola',
  'endesa',
  'naturgy',
  'repsol',
  'totalenergies',
  'total energies',
  'holaluz',
  'octopus',
  'plenitude',
  'edp',
  'audax',
  'factor',
  'gana',
  'lucera',
  'som',
  'energia xxi',
  'xxi',
  'curenergia',
  'regulada',
  'bonpreu',
];

/** Comercios que solo pueden ser una cosa (o casi). */
const COMERCIOS: readonly Regla[] = [
  {
    tipo: 'gas',
    palabras: ['redexis', 'nedgia', 'butano', 'moeve', 'cepsa'],
  },
  {
    tipo: 'tele',
    palabras: [
      'movistar',
      'telefonica',
      'vodafone',
      'orange',
      'masmovil',
      'mas movil',
      'masorange',
      'digi',
      'yoigo',
      'pepephone',
      'lowi',
      'o2',
      'jazztel',
      'simyo',
      'finetwork',
      'euskaltel',
      'telecable',
      'avatel',
      'adamo',
      'virgin',
      'dazn',
    ],
  },
  {
    tipo: 'suscripcion',
    palabras: [
      'netflix',
      'spotify',
      'disney',
      'hbo',
      'max',
      'prime video',
      'amazon prime',
      'icloud',
      'apple',
      'google one',
      'youtube',
      'google play',
      'microsoft',
      'dropbox',
      'audible',
      'kindle',
      'filmin',
      'tidal',
      'deezer',
      'twitch',
      'playstation',
      'xbox',
      'nintendo',
      'duolingo',
      'canva',
      'adobe',
      'openai',
      'chatgpt',
      'el pais',
      'el mundo',
      'la vanguardia',
      'patreon',
    ],
  },
  {
    tipo: 'seguro-salud',
    palabras: [
      'sanitas',
      'adeslas',
      'dkv',
      'asisa',
      'cigna',
      'aegon',
      'nectar',
      'preventiva',
      'vidacaixa',
    ],
  },
  {
    tipo: 'seguro-coche',
    palabras: [
      'linea directa',
      'mutua madrilena',
      'verti',
      'genesis',
      'qualitas',
      'balumba',
      'direct seguros',
      'nuez',
      'racc',
      'race',
      'pelayo',
    ],
  },
  {
    tipo: 'tarjeta',
    palabras: [
      'wizink',
      'cofidis',
      'oney',
      'cetelem',
      'carrefour pass',
      'pass carrefour',
      'bankinter consumer',
      'fintonic',
      'klarna',
    ],
  },
];

/** Aseguradoras de todo: el tipo sale de la categoría o del importe. */
const ASEGURADORAS = [
  'mapfre',
  'allianz',
  'axa',
  'generali',
  'zurich',
  'reale',
  'catalana occidente',
  'fiatc',
  'helvetia',
  'liberty',
  'caser',
  'santalucia',
  'santa lucia',
  'ocaso',
  'plus ultra',
  'segurcaixa',
  'bbva seguros',
  'santander seguros',
  'bansabadell',
  'seguros',
  'seguro',
  'asseguranca',
  'assegurances',
  'poliza',
];

/** Palabras de la categoría (o del comercio) que deciden el tipo. */
const PALABRAS: readonly Regla[] = [
  {
    tipo: 'seguro-coche',
    palabras: ['coche', 'cotxe', 'moto', 'auto', 'vehiculo', 'vehicle'],
  },
  {
    tipo: 'seguro-hogar',
    palabras: [
      'hogar',
      'casa',
      'vivienda',
      'llar',
      'multirriesgo',
      'habitatge',
    ],
  },
  {
    tipo: 'seguro-salud',
    palabras: [
      'salud',
      'salut',
      'medico',
      'metge',
      'dental',
      'vida',
      'decesos',
    ],
  },
  {
    tipo: 'gas',
    palabras: ['gas', 'butano', 'caldera'],
  },
  {
    tipo: 'luz',
    palabras: ['luz', 'llum', 'electricidad', 'electricitat', 'kwh', 'pvpc'],
  },
  {
    tipo: 'tele',
    palabras: [
      'movil',
      'mobil',
      'fibra',
      'internet',
      'telefono',
      'telefon',
      'telefonia',
      'linea',
      'adsl',
      'wifi',
    ],
  },
  {
    tipo: 'suscripcion',
    palabras: [
      'suscripcion',
      'suscripciones',
      'subscripcio',
      'subscripcions',
      'subscription',
      'streaming',
      'plataformas',
      'premium',
    ],
  },
  {
    tipo: 'tarjeta',
    palabras: ['tarjeta', 'targeta', 'aplazado', 'revolving', 'comision'],
  },
];

function contiene(texto: string, palabra: string): boolean {
  return texto.includes(` ${palabra} `);
}

function porPalabras(texto: string): TipoRecibo | null {
  for (const regla of PALABRAS) {
    if (regla.palabras.some(p => contiene(texto, p))) {
      return regla.tipo;
    }
  }
  return null;
}

/**
 * Tipo de un cobro por su comercio y su categoría. `null` = no se sabe
 * (la ficha pregunta). Un comercio de dos tipos (Iberdrola: luz o gas;
 * Mapfre: coche, hogar o salud) se decide por la categoría.
 */
export function reconocerTipo(
  comercio: string,
  categoria: string | null,
): TipoRecibo | null {
  const c = normalizarComercio(comercio);
  const cat = normalizarComercio(categoria ?? '');
  for (const regla of COMERCIOS) {
    if (regla.palabras.some(p => contiene(c, p))) {
      return regla.tipo;
    }
  }
  if (ELECTRICAS.some(p => contiene(c, p))) {
    // Gas solo si la categoría (o el comercio) lo dice; si no, luz.
    return porPalabras(cat) === 'gas' || contiene(c, 'gas') ? 'gas' : 'luz';
  }
  if (ASEGURADORAS.some(p => contiene(c, p))) {
    const tipo = porPalabras(cat) ?? porPalabras(c);
    return tipo != null && tipo.startsWith('seguro-') ? tipo : null;
  }
  const tipo = porPalabras(c) ?? porPalabras(cat);
  // «Casa» o «Coche» a secas no es un seguro: hace falta que lo diga.
  if (
    tipo?.startsWith('seguro-') &&
    !ASEGURADORAS.some(p => contiene(c, p) || contiene(cat, p))
  ) {
    return null;
  }
  return tipo;
}
